import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { uniqueId } from 'lodash'
import React, { FC, useEffect, useState } from 'react'
import Modal from '../common/Modal'
import { useSelector } from 'react-redux'
import { RootState } from '../../../states/containers/store'
import { compile } from '../../../utils/search'

interface ModalState {
  id?: number
  name?: string
  projectSetting?: boolean
  costView?: boolean
  memberCostSetting?: boolean
  projectCostSetting?: boolean
  attendanceExport?: boolean
  projectMemberSetting?: boolean
  type: string
}

interface Props {
  modalState: ModalState
  closeCallback: VoidFunction
  addCallback: (
    memberId: number,
    projectSetting: boolean,
    costView: boolean,
    memberCostSetting: boolean,
    projectCostSetting: boolean,
    attendanceExport: boolean,
    projectMemberSetting: boolean,
  ) => void
  modCallback: (
    memberId: number,
    projectSetting: boolean,
    costView: boolean,
    memberCostSetting: boolean,
    projectCostSetting: boolean,
    attendanceExport: boolean,
    projectMemberSetting: boolean,
  ) => void
  isDisplay: boolean
  memberList: { id: number, name: string }[]
}

const AdminModal: FC<Props> = (props) => {
  const {
    modalState,
    closeCallback,
    addCallback,
    modCallback,
    isDisplay,
    memberList,
  } = props

  const {
    login,
  } = useSelector((state: RootState) => state)

  const [projectSetting, setProjectSetting] = useState<boolean>(false)
  const [costView, setCostView] = useState<boolean>(false)
  const [memberCostSetting, setMemberCostSetting] = useState<boolean>(false)
  const [projectCostSetting, setProjectCostSetting] = useState<boolean>(false)
  const [attendanceExport, setAttendanceExport] = useState<boolean>(false)
  const [selectMemberId, setSelectMemberId] = useState<number>(0)
  const [projectMemberSetting, setProjectMemberSetting] = useState<boolean>(false)

  const [data, setData] = useState<{ id: number, name: string }[]>([])
  const [searchWord, setSearchWord] = useState<string>("")
  const [isDisplayList, setIsDisplayList] = useState<boolean>(false)

  const search = (words: string) => {
    setSearchWord(words)
    setIsDisplayList(true)
    if (!words) {
      setData(memberList)
      return
    }
    const word = words.toLowerCase().split(/\s+/)
    const tmpData = memberList.filter((value) => {
      if (value === null) return false
      return (compile(word).test(value.name.toLocaleLowerCase()))
    })
    setData(tmpData)
  }

  const reset = () => {
    setSearchWord("")
    setSelectMemberId(0)
    setData(memberList)
    setIsDisplayList(false)
  }

  const resetParam = () => {
    setSearchWord("")
    setSelectMemberId(0)
    setProjectSetting(false)
    setCostView(false)
    setMemberCostSetting(false)
    setProjectCostSetting(false)
    setAttendanceExport(false)
    setProjectMemberSetting(false)
  }

  const selectMember = (param: { id: number, name: string }) => {
    setSelectMemberId(param.id)
    setSearchWord(param.name)
    setIsDisplayList(false)
  }

  const closeModal = () => {
    closeCallback()
    resetParam()
  }

  const submitAdmin = () => {
    if (modalState.type === "create") {
      addCallback(selectMemberId, projectSetting, costView, memberCostSetting, projectCostSetting, attendanceExport, projectMemberSetting)
    } else {
      modCallback(modalState.id || 0, projectSetting, costView, memberCostSetting, projectCostSetting, attendanceExport, projectMemberSetting)
    }
    resetParam()
  }

  useEffect(() => {
    setSelectMemberId(modalState.id || 0)
    if (modalState.type === "create") {
      setProjectSetting(true)
    } else {
      setProjectSetting(modalState.projectSetting || false)
    }
    setCostView(modalState.costView || false)
    setMemberCostSetting(modalState.memberCostSetting || false)
    setProjectCostSetting(modalState.projectCostSetting || false)
    setAttendanceExport(modalState.attendanceExport || false)
    setProjectMemberSetting(modalState.projectMemberSetting || false)
    setData(memberList)
  }, [isDisplay])

  return (
    <Modal
      title={modalState.type === "create" ? "権限追加" : "権限編集"}
      show={isDisplay}
      wrapperClass="task_administrator_modal"
      closeCallback={closeModal}
      closeable
    >
      <>
        <div className="item">
          <div className="title">
            ユーザー
          </div>
          <div className="detail equal_space row_1">
            {
              modalState.type === "create" ? (
                <div className="select_search">
                  <input
                    type="text"
                    onChange={(event) => search(event.target.value)}
                    value={searchWord}
                    onFocus={() => setIsDisplayList(true)}
                    placeholder="ユーザーを検索・選択してください"
                  />
                  {
                    searchWord && (
                      <button type="button" className="reset" onClick={reset}>
                        <FontAwesomeIcon icon={['far', 'times-circle']} />
                      </button>
                    )
                  }
                  {
                    isDisplayList && (
                      <>
                        {
                          data.length ? (
                            <div className="select_list">
                              {
                                data.map((member) => (
                                  <div
                                    key={uniqueId()}
                                    onClick={() => selectMember(member)}
                                    role="none"
                                  >
                                    {member.name}
                                  </div>
                                ))
                              }
                            </div>
                          ) : (
                            <></>
                          )
                        }
                        <div className="close_screen" role="presentation" onClick={() => setIsDisplayList(false)} />
                      </>
                    )
                  }
                </div>
              ) : (
                <div
                  style={{
                    fontSize: "24px",
                  }}
                >
                  {modalState.name}
                </div>
              )
            }
          </div>
        </div>
        <div className="item">
          <div className="title">
            権限
          </div>
          <div className="detail row_1">
            <div
              className="checkbox_area"
              style={{
                backgroundColor: projectSetting ? "#d9ffdd" : "#FFFFFF",
              }}
              onClick={() => setProjectSetting(!projectSetting)}
              role="none"
            >
              <div
                className="checkbox"
                style={{
                  color: projectSetting ? "#08984C" : "#666666",
                }}
              >
                <FontAwesomeIcon icon={['far', projectSetting ? 'check-square' : 'square']} />
              </div>
              <div>
                <div className="checkbox_area_title">
                  プロジェクト・カテゴリー設定
                </div>
                <div className="checkbox_area_description">
                  プロジェクト・カテゴリーを設定・編集することができます
                </div>
              </div>
            </div>
            <div
              className="checkbox_area"
              style={{
                backgroundColor: costView ? "#d9ffdd" : "#FFFFFF",
              }}
              onClick={() => setCostView(!costView)}
              role="none"
            >
              <div
                className="checkbox"
                style={{
                  color: costView ? "#08984C" : "#666666",
                }}
              >
                <FontAwesomeIcon icon={['far', costView ? 'check-square' : 'square']} />
              </div>
              <div>
                <div className="checkbox_area_title">
                  コスト表示
                </div>
                <div className="checkbox_area_description">
                  プロジェクトにかかったコストを表示することができます
                </div>
              </div>
            </div>
            <div
              className="checkbox_area"
              style={{
                backgroundColor: memberCostSetting ? "#d9ffdd" : "#FFFFFF",
              }}
              onClick={() => setMemberCostSetting(!memberCostSetting)}
              role="none"
            >
              <div
                className="checkbox"
                style={{
                  color: memberCostSetting ? "#08984C" : "#666666",
                }}
              >
                <FontAwesomeIcon icon={['far', memberCostSetting ? 'check-square' : 'square']} />
              </div>
              <div>
                <div className="checkbox_area_title">
                  メンバーコスト設定
                </div>
                <div className="checkbox_area_description">
                  メンバーのコストを設定することができます
                </div>
              </div>
            </div>
            <div
              className="checkbox_area"
              style={{
                backgroundColor: projectCostSetting ? "#d9ffdd" : "#FFFFFF",
              }}
              onClick={() => setProjectCostSetting(!projectCostSetting)}
              role="none"
            >
              <div
                className="checkbox"
                style={{
                  color: projectCostSetting ? "#08984C" : "#666666",
                }}
              >
                <FontAwesomeIcon icon={['far', projectCostSetting ? 'check-square' : 'square']} />
              </div>
              <div>
                <div className="checkbox_area_title">
                  プロジェクトコスト設定
                </div>
                <div className="checkbox_area_description">
                  プロジェクトのコストを設定することができます
                </div>
              </div>
            </div>
            {
              login.function_enabled_state?.task_management_approval && (
                <div
                  className="checkbox_area"
                  style={{
                    backgroundColor: attendanceExport ? "#d9ffdd" : "#FFFFFF",
                  }}
                  onClick={() => setAttendanceExport(!attendanceExport)}
                  role="none"
                >
                  <div
                    className="checkbox"
                    style={{
                      color: attendanceExport ? "#08984C" : "#666666",
                    }}
                  >
                    <FontAwesomeIcon icon={['far', attendanceExport ? 'check-square' : 'square']} />
                  </div>
                  <div>
                    <div className="checkbox_area_title">
                      勤怠データエクスポート
                    </div>
                    <div className="checkbox_area_description">
                      勤怠データをCSV出力することができます
                    </div>
                  </div>
                </div>
              )
            }
            {
              login.enable_project_member_setting && (
                <div
                  className="checkbox_area"
                  style={{
                    backgroundColor: projectMemberSetting ? "#d9ffdd" : "#FFFFFF",
                  }}
                  onClick={() => setProjectMemberSetting(!projectMemberSetting)}
                  role="none"
                >
                  <div
                    className="checkbox"
                    style={{
                      color: projectMemberSetting ? "#08984C" : "#666666",
                    }}
                  >
                    <FontAwesomeIcon icon={['far', projectMemberSetting ? 'check-square' : 'square']} />
                  </div>
                  <div>
                    <div className="checkbox_area_title">
                      プロジェクト参加メンバー設定
                    </div>
                    <div className="checkbox_area_description">
                      プロジェクトの参加メンバーを設定することができます
                    </div>
                  </div>
                </div>
              )
            }
            {
              (!projectSetting && !costView && !memberCostSetting && !projectCostSetting && !attendanceExport && !projectMemberSetting) && (
                <div className="error_box">
                  ※ 権限は1つ以上選択してください
                </div>
              )
            }
          </div>
        </div>
      </>
      <>
        <button
          type="button"
          className="button cancel"
          onClick={closeModal}
        >
          キャンセル
        </button>
        <button
          type="button"
          className="button"
          onClick={submitAdmin}
          disabled={
            !selectMemberId
            || (!projectSetting && !costView && !memberCostSetting && !projectCostSetting && !attendanceExport && !projectMemberSetting)
          }
        >
          {modalState.type === "create" ? "新規作成" : "更新"}
        </button>
      </>
    </Modal>
  )
}

export default AdminModal
